import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useLocation, useFetcher } from 'react-router'
import { Command } from 'cmdk'
import { useTranslation } from 'react-i18next'
import {
  Search,
  FileText,
  Users,
  Building2,
  TrendingUp,
  CalendarCheck,
  Plus,
  Settings,
  User,
  LogOut,
  Hash,
  Calendar,
  DollarSign,
  Filter,
  Save,
  X,
  ChevronRight,
  Home,
} from 'lucide-react'
import { cn } from '#app/utils/misc.tsx'
import { useDebounce } from '#app/utils/debounce.tsx'
import { Dialog, DialogContent } from './ui/dialog.tsx'
import { Badge } from './ui/badge.tsx'
import { Button } from './ui/button.tsx'
import { Separator } from './ui/separator.tsx'
import { useHotkeys } from '#app/hooks/use-hotkeys.tsx'

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface SearchResult {
  id: string
  type: 'quote' | 'deal' | 'contact' | 'organization' | 'activity' | 'template'
  title: string
  subtitle?: string
  status?: string
  value?: number
  date?: string
  icon: any
  url: string
}

interface QuickAction {
  id: string
  title: string
  icon: any
  shortcut?: string
  action: () => void
  keywords?: string[]
}

interface FilterOption {
  id: string
  type: string
  label: string
  value: any
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const { t } = useTranslation(['app', 'common'])
  const navigate = useNavigate()
  const location = useLocation()
  const [search, setSearch] = useState('')
  const [pages, setPages] = useState<string[]>([])
  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>([])
  const debouncedSearch = useDebounce(search, 300)
  const searchFetcher = useFetcher<{ results: SearchResult[] }>()
  
  const page = pages[pages.length - 1]
  
  // Quick actions
  const quickActions: QuickAction[] = [
    {
      id: 'new-quote',
      title: t('app:quotes.newQuote'),
      icon: Plus,
      shortcut: '⌘N',
      action: () => {
        void navigate('/quotes/new')
        onOpenChange(false)
      },
      keywords: ['create', 'add', 'quote']
    },
    {
      id: 'new-deal',
      title: t('app:deals.newDeal'),
      icon: Plus,
      action: () => {
        void navigate('/deals/new')
        onOpenChange(false)
      },
      keywords: ['create', 'add', 'deal', 'opportunity']
    },
    {
      id: 'new-contact',
      title: t('app:contacts.newContact'),
      icon: Plus,
      action: () => {
        void navigate('/contacts/new')
        onOpenChange(false)
      },
      keywords: ['create', 'add', 'contact', 'person']
    },
    {
      id: 'new-organization',
      title: t('app:organizations.newOrganization'),
      icon: Plus,
      action: () => {
        void navigate('/organizations/new')
        onOpenChange(false)
      },
      keywords: ['create', 'add', 'organization', 'company']
    },
    {
      id: 'go-dashboard',
      title: t('app:navigation.dashboard'),
      icon: Home,
      shortcut: '⌘D',
      action: () => {
        void navigate('/dashboard')
        onOpenChange(false)
      },
      keywords: ['home', 'overview']
    },
    {
      id: 'go-settings',
      title: t('app:sidebar.settings'),
      icon: Settings,
      shortcut: '⌘,',
      action: () => {
        void navigate('/settings/profile')
        onOpenChange(false)
      },
      keywords: ['preferences', 'configuration']
    },
  ]
  
  // Filter presets
  const filterPresets = [
    {
      id: 'my-quotes',
      label: t('app:quotes.myQuotes'),
      filters: [{ id: 'owner', type: 'owner', label: 'My Items', value: 'me' }]
    },
    {
      id: 'recent-deals',
      label: 'Recent Deals',
      filters: [{ id: 'created', type: 'date', label: 'Last 7 days', value: '7d' }]
    },
    {
      id: 'high-value',
      label: 'High Value Opportunities',
      filters: [{ id: 'value', type: 'value', label: '> €10,000', value: 10000 }]
    },
    {
      id: 'overdue-activities',
      label: 'Overdue Activities',
      filters: [{ id: 'overdue', type: 'status', label: 'Overdue', value: true }]
    },
  ]
  
  // Search when search term changes
  useEffect(() => {
    if (debouncedSearch && open && !page) {
      void searchFetcher.load(`/resources/search?q=${encodeURIComponent(debouncedSearch)}`)
    }
  }, [debouncedSearch, open, page])
  
  // Reset when closed
  useEffect(() => {
    if (!open) {
      setSearch('')
      setPages([])
      setSelectedFilters([])
    }
  }, [open])
  
  // Keyboard navigation
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // Escape or backspace to go back
      if (open && (e.key === 'Escape' || (e.key === 'Backspace' && !search))) {
        e.preventDefault()
        if (pages.length > 0) {
          setPages(pages.slice(0, -1))
        } else {
          onOpenChange(false)
        }
      }
    }
    
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [open, search, pages, onOpenChange])
  
  const runCommand = useCallback((command: () => void) => {
    onOpenChange(false)
    command()
  }, [onOpenChange])
  
  const applyFilters = () => {
    // Build query params from filters
    const params = new URLSearchParams()
    selectedFilters.forEach(filter => {
      params.append(filter.type, filter.value)
    })
    
    // Navigate with filters
    const currentPath = location.pathname
    void navigate(`${currentPath}?${params.toString()}`)
    onOpenChange(false)
  }
  
  const toggleFilter = (filter: FilterOption) => {
    setSelectedFilters(prev => {
      const exists = prev.find(f => f.id === filter.id)
      if (exists) {
        return prev.filter(f => f.id !== filter.id)
      }
      return [...prev, filter]
    })
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 shadow-2xl" showClose={false}>
        <Command 
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
          loop
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              onOpenChange(false)
            }
          }}
        >
          <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder={page === 'filters' ? t('common:search') + ' filters...' : t('common:search') + '...'}
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
            {selectedFilters.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {selectedFilters.length} filters
              </Badge>
            )}
          </div>
          <Command.List className="max-h-[400px] overflow-y-auto overflow-x-hidden">
            <Command.Empty className="py-6 text-center text-sm">
              {search ? `No results found for "${search}"` : 'Start typing to search...'}
            </Command.Empty>
            
            {!page && (
              <>
                {/* Quick Actions */}
                {!search && (
                  <Command.Group heading="Quick Actions">
                    {quickActions.map((action) => (
                      <Command.Item
                        key={action.id}
                        value={action.title}
                        keywords={action.keywords}
                        onSelect={() => runCommand(action.action)}
                      >
                        <action.icon className="mr-2 h-4 w-4" />
                        <span>{action.title}</span>
                        {action.shortcut && (
                          <kbd className="ml-auto text-xs text-muted-foreground">
                            {action.shortcut}
                          </kbd>
                        )}
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}
                
                {/* Search Results */}
                {search && searchFetcher.data?.results && searchFetcher.data.results.length > 0 && (
                  <Command.Group heading="Search Results">
                    {searchFetcher.data.results.map((result) => (
                      <Command.Item
                        key={result.id}
                        value={result.title}
                        onSelect={() => {
                          void navigate(result.url)
                          onOpenChange(false)
                        }}
                      >
                        <result.icon className="mr-2 h-4 w-4" />
                        <div className="flex flex-1 items-center justify-between">
                          <div>
                            <span>{result.title}</span>
                            {result.subtitle && (
                              <span className="ml-2 text-sm text-muted-foreground">
                                {result.subtitle}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {result.status && (
                              <Badge variant="secondary" className="text-xs">
                                {result.status}
                              </Badge>
                            )}
                            {result.value && (
                              <span className="text-sm text-muted-foreground">
                                €{result.value.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}
                
                {/* Navigation */}
                {!search && (
                  <Command.Group heading="Navigation">
                    <Command.Item
                      value="Quotes"
                      onSelect={() => {
                        void navigate('/quotes')
                        onOpenChange(false)
                      }}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      <span>{t('app:quotes.title')}</span>
                    </Command.Item>
                    <Command.Item
                      value="Deals"
                      onSelect={() => {
                        void navigate('/deals')
                        onOpenChange(false)
                      }}
                    >
                      <TrendingUp className="mr-2 h-4 w-4" />
                      <span>{t('app:navigation.deals')}</span>
                    </Command.Item>
                    <Command.Item
                      value="Contacts"
                      onSelect={() => {
                        void navigate('/contacts')
                        onOpenChange(false)
                      }}
                    >
                      <Users className="mr-2 h-4 w-4" />
                      <span>{t('app:navigation.contacts')}</span>
                    </Command.Item>
                    <Command.Item
                      value="Organizations"
                      onSelect={() => {
                        void navigate('/organizations')
                        onOpenChange(false)
                      }}
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      <span>{t('app:navigation.organizations')}</span>
                    </Command.Item>
                  </Command.Group>
                )}
                
                {/* Advanced Options */}
                {!search && (
                  <Command.Group heading="Advanced">
                    <Command.Item
                      value="Filters"
                      onSelect={() => setPages([...pages, 'filters'])}
                    >
                      <Filter className="mr-2 h-4 w-4" />
                      <span>Advanced Filters</span>
                      <ChevronRight className="ml-auto h-4 w-4" />
                    </Command.Item>
                    <Command.Item
                      value="Saved Views"
                      onSelect={() => setPages([...pages, 'saved-views'])}
                    >
                      <Save className="mr-2 h-4 w-4" />
                      <span>Saved Views</span>
                      <ChevronRight className="ml-auto h-4 w-4" />
                    </Command.Item>
                  </Command.Group>
                )}
              </>
            )}
            
            {/* Filters Page */}
            {page === 'filters' && (
              <>
                <Command.Group heading="Quick Filters">
                  {filterPresets.map((preset) => (
                    <Command.Item
                      key={preset.id}
                      value={preset.label}
                      onSelect={() => {
                        setSelectedFilters(preset.filters)
                        setPages([])
                      }}
                    >
                      <Hash className="mr-2 h-4 w-4" />
                      <span>{preset.label}</span>
                    </Command.Item>
                  ))}
                </Command.Group>
                
                <Command.Group heading="Filter by Type">
                  <Command.Item
                    value="My Items"
                    onSelect={() => toggleFilter({ id: 'owner', type: 'owner', label: 'My Items', value: 'me' })}
                  >
                    <User className="mr-2 h-4 w-4" />
                    <span>My Items</span>
                    {selectedFilters.find(f => f.id === 'owner') && (
                      <Badge className="ml-auto" variant="secondary">Selected</Badge>
                    )}
                  </Command.Item>
                  <Command.Item
                    value="Last 7 Days"
                    onSelect={() => toggleFilter({ id: 'created', type: 'date', label: 'Last 7 days', value: '7d' })}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    <span>Last 7 Days</span>
                    {selectedFilters.find(f => f.id === 'created') && (
                      <Badge className="ml-auto" variant="secondary">Selected</Badge>
                    )}
                  </Command.Item>
                  <Command.Item
                    value="High Value"
                    onSelect={() => toggleFilter({ id: 'value', type: 'value', label: '> €10,000', value: 10000 })}
                  >
                    <DollarSign className="mr-2 h-4 w-4" />
                    <span>High Value (&gt; €10,000)</span>
                    {selectedFilters.find(f => f.id === 'value') && (
                      <Badge className="ml-auto" variant="secondary">Selected</Badge>
                    )}
                  </Command.Item>
                </Command.Group>
              </>
            )}
            
            {/* Saved Views Page */}
            {page === 'saved-views' && (
              <Command.Group heading="Your Saved Views">
                <Command.Item value="Pipeline Overview">
                  <TrendingUp className="mr-2 h-4 w-4" />
                  <span>Pipeline Overview</span>
                </Command.Item>
                <Command.Item value="Monthly Report">
                  <FileText className="mr-2 h-4 w-4" />
                  <span>Monthly Report</span>
                </Command.Item>
                <Command.Item value="Team Performance">
                  <Users className="mr-2 h-4 w-4" />
                  <span>Team Performance</span>
                </Command.Item>
              </Command.Group>
            )}
          </Command.List>
          
          {/* Footer with selected filters */}
          {selectedFilters.length > 0 && (
            <div className="border-t p-3">
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  {selectedFilters.map((filter) => (
                    <Badge
                      key={filter.id}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      {filter.label}
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          toggleFilter(filter)
                        }}
                        className="ml-1 rounded-sm hover:bg-muted"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedFilters([])}
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={applyFilters}
                  >
                    Apply Filters
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Command>
      </DialogContent>
    </Dialog>
  )
}